import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { FileText, Calendar, User, CheckCircle, Clock, Send, Save } from 'lucide-react';
import { toast } from 'sonner';

const BilanMensuelEntreprise = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [selectedMois, setSelectedMois] = useState('');
  const [bilanContenu, setBilanContenu] = useState('');
  const [bilansExistants, setBilansExistants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Déterminer les mois disponibles pour le bilan
  const getMoisDisponibles = () => {
    const now = new Date();
    const mois = [];
    
    // Bilan fin juillet (disponible du 25 juillet au 31 juillet)
    if (now.getMonth() === 6 && now.getDate() >= 25) {
      mois.push({ value: 'juillet', label: 'Bilan Fin Juillet', annee: now.getFullYear() });
    }
    
    // Bilan fin août (disponible du 25 août au 31 août)
    if (now.getMonth() === 7 && now.getDate() >= 25) {
      mois.push({ value: 'aout', label: 'Bilan Fin Août', annee: now.getFullYear() });
    }
    
    // Pour les tests, on ajoute toujours les deux options
    if (mois.length === 0) {
      mois.push(
        { value: 'juillet', label: 'Bilan Fin Juillet', annee: now.getFullYear() },
        { value: 'aout', label: 'Bilan Fin Août', annee: now.getFullYear() }
      );
    }
    
    return mois;
  };

  const moisDisponibles = getMoisDisponibles();

  useEffect(() => {
    loadStagiaires();
  }, []);

  useEffect(() => {
    if (selectedStagiaire) {
      loadBilansExistants();
    }
  }, [selectedStagiaire]);

  useEffect(() => {
    // Charger le contenu existant si bilan déjà fait
    if (selectedStagiaire && selectedMois) {
      const bilanExistant = bilansExistants.find(
        b => b.mois === selectedMois
      );
      if (bilanExistant) {
        setBilanContenu(bilanExistant.contenu);
      } else {
        setBilanContenu('');
      }
    }
  }, [selectedMois, bilansExistants]);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        setSelectedStagiaire(response.data[0].user_id);
      }
      if (moisDisponibles.length > 0) {
        setSelectedMois(moisDisponibles[0].value);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadBilansExistants = async () => {
    try {
      const response = await axios.get(`/bilans-entreprise/stagiaire/${selectedStagiaire}`);
      setBilansExistants(response.data);
    } catch (error) {
    }
  };

  const handleSubmitBilan = async () => {
    if (!selectedStagiaire || !selectedMois || !bilanContenu.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const moisInfo = moisDisponibles.find(m => m.value === selectedMois);
      
      await axios.post('/bilans-entreprise', {
        stagiaire_id: selectedStagiaire,
        mois: selectedMois,
        annee: moisInfo?.annee || new Date().getFullYear(),
        contenu: bilanContenu
      });

      toast.success('Bilan mensuel enregistré et envoyé au centre de formation');
      loadBilansExistants();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du bilan');
    }
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);
  const bilanDejaFait = bilansExistants.some(b => b.mois === selectedMois);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="bilan-mensuel-entreprise">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Bilan Mensuel</h1>
        <p className="text-slate-600">Évaluez votre stagiaire à la fin de chaque mois</p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Périodes de bilan</p>
            <p>Les bilans sont à remplir :</p>
            <ul className="list-disc ml-5 mt-1">
              <li>Fin <strong>Juillet</strong> (du 25 au 31 juillet)</li>
              <li>Fin <strong>Août</strong> (du 25 au 31 août)</li>
            </ul>
            <p className="mt-2 text-blue-600">Ces bilans sont transmis au centre de formation et au stagiaire.</p>
          </div>
        </div>
      </div>

      {/* Sélection Stagiaire et Mois */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="stagiaire">Stagiaire</Label>
            <select
              id="stagiaire"
              value={selectedStagiaire}
              onChange={(e) => setSelectedStagiaire(e.target.value)}
              className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            >
              {stagiaires.length === 0 ? (
                <option value="">Aucun stagiaire assigné</option>
              ) : (
                stagiaires.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mois">Période du bilan</Label>
            <select
              id="mois"
              value={selectedMois}
              onChange={(e) => setSelectedMois(e.target.value)}
              className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            >
              {moisDisponibles.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label} {m.annee}
                </option>
              ))}
            </select>
          </div>
        </div>

        {currentStagiaire && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg flex items-center space-x-4">
            <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#0F172A]">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-sm text-slate-600">
                {currentStagiaire.centre_formation} - {currentStagiaire.statut}
              </p>
            </div>
            {bilanDejaFait && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Bilan déjà soumis</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Formulaire Bilan */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F]">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#8DC63F]" />
          Votre Évaluation
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Décrivez votre expérience avec le stagiaire, ses points forts, ses axes d'amélioration, 
          son comportement professionnel, sa progression...
        </p>

        <div className="space-y-4">
          <textarea
            value={bilanContenu}
            onChange={(e) => setBilanContenu(e.target.value)}
            placeholder="Écrivez votre bilan ici...

Vous pouvez aborder les points suivants :
- Comportement général et attitude professionnelle
- Compétences techniques acquises
- Points forts observés
- Axes d'amélioration
- Recommandations pour la suite de la formation
- Toute autre observation pertinente"
            className="w-full h-64 px-4 py-3 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="bilan-textarea"
          />

          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {bilanContenu.length} caractères
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleSubmitBilan}
                className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
                disabled={!bilanContenu.trim()}
              >
                <Send className="h-4 w-4 mr-2" />
                {bilanDejaFait ? 'Mettre à jour le bilan' : 'Envoyer le bilan'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Historique des bilans */}
      {bilansExistants.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-500" />
            Historique des Bilans
          </h2>
          <div className="space-y-4">
            {bilansExistants.map((bilan, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#0F172A]">
                    Bilan {bilan.mois === 'juillet' ? 'Juillet' : 'Août'} {bilan.annee}
                  </span>
                  <span className="text-sm text-slate-500">
                    Envoyé le {new Date(bilan.date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{bilan.contenu}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BilanMensuelEntreprise;
